import type { ICmlService } from './ICmlService.interface'
import type { CmlRef, CmlComparison, CmlDeployResult, CmlDependencyClosure, CmlDependency, CmlDeployPreview } from '../../types/cml.types'

// ── Seeded demo data (UI dev without a backend) ───────────────────────────────
const SRC = `type residentialinternet : LineItem {
  relation modem : LineItem;
  constraint modem.qty == 1;
}`
const TGT = `type residentialinternet : LineItem {
  relation modem : LineItem;
  constraint modem.qty >= 1;
}`

const ROWS: CmlComparison['rows'] = [
  { developerName: 'Residential_Internet_V1', esdDeveloperName: 'Residential_Internet', change: 'changed',   sourceCml: SRC, targetCml: TGT, sourceConstraintCount: 5, targetConstraintCount: 5 },
  { developerName: 'Fiber_Broadband_V1',      esdDeveloperName: 'Fiber_Broadband',      change: 'added',     sourceCml: 'type fiberbroadband : LineItem {}', sourceConstraintCount: 3 },
  { developerName: 'Legacy_DSL_V1',           esdDeveloperName: 'Legacy_DSL',           change: 'removed',   targetCml: 'type legacydsl : LineItem {}', targetConstraintCount: 2 },
  { developerName: 'Internet_Services_V1',    esdDeveloperName: 'Internet_Services',    change: 'identical', sourceCml: 'type internetservices : LineItem {}', targetCml: 'type internetservices : LineItem {}', sourceConstraintCount: 1, targetConstraintCount: 1 },
]

/** Mock provider — seeded rows so the CML Migration page renders without an org. */
export class MockCmlService implements ICmlService {
  list(): Promise<CmlRef[]> {
    return Promise.resolve(ROWS.filter((r) => r.change !== 'removed').map((r) => ({
      esdvId: `9QB${r.developerName}`, developerName: r.developerName, esdDeveloperName: r.esdDeveloperName,
      versionNumber: 1, status: 'Active', masterLabel: r.developerName.replace(/_/g, ' '), hasConstraintModel: true,
    })))
  }

  compare(): Promise<CmlComparison> {
    return Promise.resolve({ rows: ROWS })
  }

  listBranch(): Promise<CmlRef[]> {
    return this.list()
  }

  compareBranch(): Promise<CmlComparison> {
    return Promise.resolve({ rows: ROWS })
  }

  compareToBranch(): Promise<CmlComparison> {
    return Promise.resolve({ rows: ROWS })
  }

  compareBranchToBranch(): Promise<CmlComparison> {
    return Promise.resolve({ rows: ROWS })
  }

  dependencies(input: { esdDeveloperName: string; targetConnectionId?: string }): Promise<CmlDependencyClosure> {
    const t = !!input.targetConnectionId
    const d = (kind: CmlDependency['kind'], object: string, naturalKey: string, group: CmlDependency['group'], matchedBy: CmlDependency['matchedBy'], present?: boolean): CmlDependency =>
      ({ kind, object, naturalKey, group, matchedBy, ...(t && present !== undefined && { presentInTarget: present }) })
    return Promise.resolve({
      developerName: `${input.esdDeveloperName}_V1`, esdDeveloperName: input.esdDeveloperName, resolvedAgainstTarget: t,
      cml: `type ${input.esdDeveloperName.toLowerCase()} : LineItem {\n  relation modem : LineItem;\n  constraint modem.qty == 1;\n}`,
      constraintCount: 5,
      dependencies: [
        d('expressionSet', 'ExpressionSet', input.esdDeveloperName, 'structural', 'ApiName', false),
        d('expressionSetDefinition', 'ExpressionSetDefinition', input.esdDeveloperName, 'structural', 'DeveloperName', false),
        d('contextDefinition', 'ContextDefinition', 'SalesTransactionContextExt_256', 'structural', 'DeveloperName', false),
        d('product', 'Product2', 'PRD_RES_INTERNET', 'catalog', 'ProductCode', true),
        d('product', 'Product2', 'PRD_ELITE_WIFI', 'catalog', 'ProductCode', true),
        d('component', 'ProductRelatedComponent', 'PRD_RES_INTERNET|PRD_ELITE_WIFI||Bundle to Bundle Component Relationship|20', 'catalog', 'composite', false),
        d('group', 'ProductComponentGroup', 'PRD_RES_INTERNET|Value Added Service|', 'catalog', 'composite', true),
        d('attribute', 'AttributeDefinition', 'INTERNET_SPEED_RES', 'catalog', 'DeveloperName', true),
        d('contextPath', 'ContextDefinition', 'SalesTransaction.SalesTransaction_InternetBandwidth__c', 'context', 'path'),
      ],
    })
  }

  deploy(input: { esdDeveloperName: string }): Promise<CmlDeployResult> {
    return Promise.resolve({
      developerName: `${input.esdDeveloperName}_V1`, expressionSetId: 'mockEsId',
      constraintsCreated: 5, constraintsDeleted: 4, blobUploaded: true, unresolved: [],
      attributeRemap: { applied: [{ developerName: 'INTERNET_SPEED_RES', to: 'INTERNET_SPEED_RES1', matchedBy: 'globalKey' }], unresolved: [] },
    })
  }

  preview(input: { esdDeveloperName: string }): Promise<CmlDeployPreview> {
    // Seeded drift: the target renamed INTERNET_SPEED_RES → INTERNET_SPEED_RES1 (same GlobalKey).
    const base = `type ${input.esdDeveloperName.toLowerCase()} : LineItem {\n  string INTERNET_SPEED_RES = ["100","500","1000"];\n  constraint (INTERNET_SPEED_RES == "1000") { };\n}`
    return Promise.resolve({
      developerName: `${input.esdDeveloperName}_V1`,
      baseCml: base,
      remappedCml: base.replace(/INTERNET_SPEED_RES\b/g, 'INTERNET_SPEED_RES1'),
      attributeRemap: { applied: [{ developerName: 'INTERNET_SPEED_RES', to: 'INTERNET_SPEED_RES1', matchedBy: 'globalKey' }], unresolved: [] },
    })
  }
}
